import { useMemo } from "react";
import { useFirestore } from "hooks/firestore/v2/useFirestore";
import { creditCard } from "hooks/firestore/v2/models/creditCard";
import { useIsApplePayAvailable } from "hooks/payments/useApplePay";
import { useIsGooglePayAvailable } from "hooks/payments/useIsGooglePayAvailable";
import {
  isBefore,
  isValid,
  parse,
  parseISO,
  startOfMonth,
  subYears,
} from "date-fns";

const randomDateInPast = subYears(new Date(), 10).toISOString();

const isCardExpired = (expiryDate: string) => {
  const cutoffDate = startOfMonth(new Date());

  const parsedDate = parse(expiryDate, "MM/yy", cutoffDate);
  const date = isValid(parsedDate) ? parsedDate : new Date(1999, 1, 1);

  return isBefore(date, cutoffDate);
};

const useCreditCards = () => {
  const {
    data: creditCards,
    isLoading,
    error,
  } = useFirestore("/users/{userId}/creditCards", creditCard.array());

  const sortedByLastSuccessfulDepositAt = useMemo(() => {
    if (isLoading) {
      return [];
    }

    return (creditCards || []).slice().sort((a, b) => {
      // sort by deposit date (newest first)
      if (a.lastSuccessfulDepositAt && b.lastSuccessfulDepositAt) {
        const aDepositDate = parseISO(a.lastSuccessfulDepositAt);
        const bDepositDate = parseISO(b.lastSuccessfulDepositAt);
        return bDepositDate.getTime() - aDepositDate.getTime();
      }

      // prioritise the one with deposit date
      if (a.lastSuccessfulDepositAt && !b.lastSuccessfulDepositAt) return -1;
      if (!a.lastSuccessfulDepositAt && b.lastSuccessfulDepositAt) return 1;

      // If neither has deposit date, sort by createdAt (oldest first)
      const aCreatedDate = parseISO(a.createdAt || randomDateInPast);
      const bCreatedDate = parseISO(b.createdAt || randomDateInPast);
      return aCreatedDate.getTime() - bCreatedDate.getTime();
    });
  }, [creditCards]);

  return {
    creditCards: sortedByLastSuccessfulDepositAt,
    isLoading,
    error,
  };
};

const useCreditCardsWithDeposit = () => {
  const { creditCards, isLoading } = useCreditCards();

  if (isLoading) return;

  return creditCards.filter(
    (card) =>
      (card.successfulDepositCount || 0) > 0 &&
      !isCardExpired(`${card.expiryMonth}/${card.expiryYear}`),
  );
};

const useIsQuickDepositAvailable = () => {
  const isGPayAvailable = useIsGooglePayAvailable();
  const isApplePayAvailable = useIsApplePayAvailable();
  const cardsWithDeposit = useCreditCardsWithDeposit();

  return isGPayAvailable || isApplePayAvailable
    ? true
    : (cardsWithDeposit?.length ?? 0) > 0;
};

type CreditCard = Exclude<
  ReturnType<typeof useCreditCards>["creditCards"][0],
  undefined
>;

export {
  useCreditCards,
  useCreditCardsWithDeposit,
  useIsQuickDepositAvailable,
  type CreditCard,
};
